import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import { getThemeById } from '../../config/themes'
import { getRoundImage, lotteryService } from '../../services/lotteryService'
import * as walletService from '../../services/walletService'
import { useLotteryCountdown } from '../../hooks/useLotteryCountdown'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'
import ModernLobbyHuayBetSideLeft, { type HuayBetItem } from './ModernLobbyHuayBetSideLeft'
import ModernLobbyHuayBetSideRight from './ModernLobbyHuayBetSideRight'
import type { FromWinNumberItem } from './ModernLobbyHuayWinNumberPage'

/** state ตอน navigate มาหน้า bet (จาก yeekee, listall/lottery-terms จาก API) */
export interface HuayBetPageState {
  backTo?: string
  backLabel?: string
  title?: string
  logo?: string
  round?: string
  time?: string
  close?: string
  /** รอบจาก API (จาก listall → lottery-terms → bet) ใช้สำหรับส่งโพย */
  roundFromApi?: import('../../services/lotteryService').LotteryRound
  /** แพ็คเกจที่เลือกจาก lottery-terms (เดียวกับ user-classic) */
  package?: import('./ModernLobbyHuayLotteryTermsPage').BettingPackage
  /** เลขวินจากหน้าจับวิน — นำไปใส่ตารางแทง */
  fromWinNumber?: FromWinNumberItem[]
}

/** แมปประเภทจากจับวิน → prizeCode + multiplier เริ่มต้น (เมื่อยังไม่มี package) */
const WIN_NUMBER_TYPE_TO_BET: Record<string, { prizeCode: string; multiplier: number }> = {
  '2 ตัวบน': { prizeCode: 'top2', multiplier: 100 },
  '2 ตัวโต๊ด': { prizeCode: 'tod2', multiplier: 13 },
  '2 ตัวล่าง': { prizeCode: 'down2', multiplier: 100 },
  '3 ตัวบน': { prizeCode: 'top3', multiplier: 1000 },
  '3 ตัวโต๊ด': { prizeCode: 'tod3', multiplier: 150 },
  '3 ตัวล่าง': { prizeCode: 'down3', multiplier: 100 },
}

const generateBetItemId = () => `bet_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`

function translateLotteryBetError(message?: string, errors?: Array<{ reason?: string; number?: string }>): string {
  const rawMessage = String(message || '').trim()
  const firstError = Array.isArray(errors) ? errors.find((item) => item?.reason || item?.number) : null
  const reason = String(firstError?.reason || '').trim()
  const lowerReason = reason.toLowerCase()
  const lowerMessage = rawMessage.toLowerCase()
  const number = String(firstError?.number || '').trim()

  if (/not available in package|ไม่รองรับในแพ็กเกจ|betting package not found|package not found|ไม่พบแพ็กเกจการเดิมพัน/i.test(rawMessage)) {
    return 'แพ็กเกจนี้ไม่ตรงกับรอบปัจจุบัน กรุณาเลือกแพ็กเกจใหม่'
  }

  if (
    /account_betting_blocked|blocked from betting|กลุ่มจำกัดการแทง|บัญชีนี้ถูกบล็อกการแทง|บัญชีนี้ยังไม่สามารถทำรายการแทงได้/i.test(rawMessage) ||
    /account_betting_blocked|blocked from betting|กลุ่มจำกัดการแทง|บัญชีนี้ถูกบล็อกการแทง|บัญชีนี้ยังไม่สามารถทำรายการแทงได้/i.test(reason)
  ) {
    return 'บัญชีนี้ยังไม่สามารถทำรายการแทงได้ กรุณาติดต่อเจ้าหน้าที่'
  }

  if (
    lowerReason.includes('number is blocked or full') ||
    lowerReason.includes('number has reached its betting limit') ||
    lowerReason.includes('would exceed total betting limit') ||
    lowerMessage.includes('some bet items are no longer valid')
  ) {
    return number
      ? `เลข ${number} ไม่สามารถซื้อได้แล้ว กรุณาลบออกจากรายการแล้วลองใหม่`
      : 'มีบางรายการไม่สามารถซื้อได้แล้ว กรุณาลบเลขที่มีปัญหาออกแล้วลองใหม่'
  }

  if (lowerReason.includes('not active')) return 'รายการนี้ปิดรับแทงแล้ว กรุณาลบออกแล้วลองใหม่'
  if (lowerReason.includes('prize type not found')) return 'ไม่พบประเภทรางวัลของรายการนี้ กรุณาลบออกแล้วลองใหม่'
  if (lowerReason.includes('invalid number length')) return 'รูปแบบเลขไม่ถูกต้อง กรุณาลบรายการนี้แล้วเลือกใหม่'
  if (
    lowerMessage.includes('bad gateway') ||
    lowerMessage.includes('service unavailable') ||
    lowerMessage.includes('gateway timeout') ||
    lowerMessage.includes('not valid json') ||
    lowerMessage.includes('unexpected token <') ||
    lowerMessage.includes('wallet_temporarily_unavailable') ||
    lowerMessage.includes('ระบบกระเป๋าเงินไม่พร้อมใช้งานชั่วคราว')
  ) {
    return 'ระบบรับแทงขัดข้องชั่วคราว กรุณาลองใหม่อีกครั้ง'
  }

  return rawMessage || 'เกิดข้อผิดพลาดในการส่งโพย'
}

/** หน้าแทงหวย (จาก huaybet.html) — แบ่ง Left/Right คล้าย user-classic LotteryBetPage, UI เป็น user-modern */
const ModernLobbyHuayBetPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state ?? {}) as HuayBetPageState
  const { themeConfig, assetConfig } = useOutletContext<LobbyHuayOutletContext>()

  const getAssetUrl = useMemo(() => {
    const folder = getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue'
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${folder}` : `/${folder}`
    return (path: string) => base + (path.startsWith('/') ? path : `/${path}`)
  }, [themeConfig.themeId, assetConfig.CDN_BASE_URL])

  const backTo = state.backTo ?? '/lobby-huay/listall'
  const backLabel = state.backLabel ?? 'กลับหน้าหลัก'
  const title = state.title ?? 'หวย'
  const roundFromApi = state.roundFromApi
  const selectedPackage = state.package
  const logoRaw = state.logo ?? '/img/logo.png'
  const logo = useMemo(() => {
    if (roundFromApi?.huayImageURL && /^https?:\/\//i.test(roundFromApi.huayImageURL)) return roundFromApi.huayImageURL
    const path = roundFromApi ? getRoundImage(roundFromApi) : logoRaw
    if (/^https?:\/\//i.test(path)) return path
    return getAssetUrl(path.startsWith('/') ? path : `/${path}`)
  }, [roundFromApi, logoRaw, getAssetUrl])
  const roundLabel = state.round ?? (roundFromApi?.roundNumber ? `รอบ ${roundFromApi.roundNumber}` : 'รอบ 22:45:00 น.')
  const countdownText = useLotteryCountdown(roundFromApi?.betEndTime, state.time ?? '3 วัน 16:39:37')

  const [selectedBetType, setSelectedBetType] = useState<string>('')
  const [numbers, setNumbers] = useState<string[]>(['', '', '', '', ''])
  const [betItems, setBetItems] = useState<HuayBetItem[]>([])
  const [defaultAmount, setDefaultAmount] = useState(1)
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [isLoadingWallet, setIsLoadingWallet] = useState(true)
  const walletBalance = walletService.getDisplayBalance(walletData)

  useEffect(() => {
    walletService.getWallet().then((res) => {
      if (res.success && res.data) setWalletData(res.data)
      setIsLoadingWallet(false)
    })
  }, [])

  const handleNumberInput = useCallback((index: number, value: string) => {
    setNumbers((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }, [])

  const appendDigit = useCallback((d: number) => {
    const str = d.toString()
    setNumbers((prev) => {
      const next = [...prev]
      for (let i = 0; i < next.length; i++) {
        if (next[i] === '') {
          next[i] = str
          return next
        }
      }
      return next
    })
  }, [])

  const backspace = useCallback(() => {
    setNumbers((prev) => {
      const next = [...prev]
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i] !== '') {
          next[i] = ''
          return next
        }
      }
      return next
    })
  }, [])

  const addBetItem = useCallback(
    (data: { number: string; betType: string; betTypeLabel: string; amount: number; multiplier: number; discount: number }) => {
      const payout = data.amount * data.multiplier
      setBetItems((prev) => [
        ...prev,
        {
          id: generateBetItemId(),
          number: data.number,
          betType: data.betType,
          betTypeLabel: data.betTypeLabel,
          amount: data.amount,
          multiplier: data.multiplier,
          discount: data.discount,
          payout: Math.round(payout * 100) / 100,
        },
      ])
    },
    []
  )

  /** นำเลขวินจากหน้าจับวิน (จาก state.fromWinNumber) ใส่ตารางแทง */
  useEffect(() => {
    const fromWinNumber = state.fromWinNumber
    if (!fromWinNumber?.length) return
    const defaultAmount = 1
    const discount = 0
    for (const item of fromWinNumber) {
      const meta = WIN_NUMBER_TYPE_TO_BET[item.type]
      if (!meta) continue
      addBetItem({
        number: item.number,
        betType: meta.prizeCode,
        betTypeLabel: item.type,
        amount: defaultAmount,
        multiplier: meta.multiplier,
        discount,
      })
    }
    const { fromWinNumber: _, ...restState } = state
    navigate(location.pathname, { replace: true, state: restState })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- ใช้เฉพาะครั้งแรกเมื่อมี fromWinNumber
  }, [state.fromWinNumber])

  const removeBetItem = useCallback((id: string) => {
    setBetItems((prev) => prev.filter((item) => item.id !== id))
  }, [])

  const updateBetAmount = useCallback((id: string, amount: number) => {
    const value = Math.max(1, amount)
    setBetItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item
        const payout = value * item.multiplier
        return { ...item, amount: value, payout: Math.round(payout * 100) / 100 }
      })
    )
  }, [])

  const clearAllBetItems = useCallback(() => {
    setBetItems([])
  }, [])

  /** หลังส่งโพยสำเร็จ เรียกให้ SideLeft ล้างประเภทการแทงและรายการที่เลือก */
  const clearLeftSelectionRef = useRef<(() => void) | null>(null)
  const [isSubmittingBet, setIsSubmittingBet] = useState(false)

  const betTypeToPrizeTypeCode = useCallback((betType: string, betTypeLabel?: string): string => {
    if (betTypeLabel === 'ปักหลักร้อย') return 'FIXED_POSITION_HUNDREDS'
    if (betTypeLabel === 'ปักหลักสิบ') return 'FIXED_POSITION_TENS'
    if (betTypeLabel === 'ปักหลักหน่วย') return 'FIXED_POSITION_ONES'
    const mapping: Record<string, string> = {
      top3: '3_DIGIT_TOP', down3: '3_DIGIT_BOTTOM', tod3: '3_DIGIT_TOD', tod3reverse: '3_DIGIT_REVERSE',
      top2: '2_DIGIT_TOP', down2: '2_DIGIT_BOTTOM', tod2: '2_DIGIT_TOD', tod2reverse: '2_DIGIT_REVERSE',
      top1: 'RUNNING_TOP', down1: 'RUNNING_BOTTOM',
      top4: '4_DIGIT_TOP', tod4: '4_DIGIT_TOD', tod5: '5_DIGIT_TOD',
      puk1: 'FIXED_POSITION_ONES', puk10: 'FIXED_POSITION_TENS', puk100: 'FIXED_POSITION_HUNDREDS',
    }
    return mapping[betType] ?? betType.toUpperCase()
  }, [])

  const prizeTypeCodeForApi = useCallback((prizeTypeCode: string): string => {
    if (prizeTypeCode === '3_DIGIT_REVERSE') return '3_DIGIT_TOP'
    if (prizeTypeCode === '2_DIGIT_REVERSE') return '2_DIGIT_TOP'
    return prizeTypeCode
  }, [])

  const submitBetItems = useCallback(async () => {
    if (isSubmittingBet) return
    if (betItems.length === 0) return
    if (!roundFromApi?._id) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'ไม่พบข้อมูลรอบหวย' })
      return
    }

    let userId: string | null = null
    let username: string | undefined
    const authToken = localStorage.getItem('accessToken')
    if (authToken) {
      try {
        const payload = JSON.parse(atob(authToken.split('.')[1]))
        userId = payload.sub ?? payload.id ?? payload.userId ?? null
        username = payload.username
      } catch {
        // ignore
      }
    }
    if (!username) {
      try {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const u = JSON.parse(userStr) as { username?: string }
          username = u?.username
        }
      } catch {
        // ignore
      }
    }
    if (!userId) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่' })
      return
    }

    try {
      setIsSubmittingBet(true)
      const betItemsForApi = betItems.map((item) => {
        const code = betTypeToPrizeTypeCode(item.betType, item.betTypeLabel)
        return {
          prizeTypeCode: prizeTypeCodeForApi(code),
          number: item.number,
          amount: item.amount,
        }
      })

      const result = await lotteryService.placeBet(
        userId,
        roundFromApi._id,
        selectedPackage?.code ?? null,
        betItemsForApi,
        'web',
        username
      )

      if (result.success) {
        clearAllBetItems()
        setSelectedBetType('')
        clearLeftSelectionRef.current?.()

        walletService.getWallet().then((res) => {
          if (res.success && res.data) setWalletData(res.data)
        })

        const modalResult = await Swal.fire({
          title: 'ส่งโพยเรียบร้อย',
          text: 'กรุณาตรวจสอบโพยหลังจากที่ส่งทุกครั้ง',
          icon: 'success',
          showCancelButton: true,
          confirmButtonText: 'ดูโพย',
          cancelButtonText: 'แทงต่อ',
          confirmButtonColor: '#28a745',
          cancelButtonColor: '#fd7e14',
          reverseButtons: true,
        })

        if (modalResult.isConfirmed) {
          navigate('/lobby-huay/slip')
        }
      } else {
        throw new Error(result.message ?? 'Failed to place bet')
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Array<{ reason?: string; number?: string }> } }; details?: { message?: string; errors?: Array<{ reason?: string; number?: string }> }; message?: string }
      const responseData = error.response?.data || error.details
      const message = translateLotteryBetError(responseData?.message || error.message, responseData?.errors)
      Swal.fire({ toast: true, position: 'top', timer: 4000, icon: 'error', title: message })
    } finally {
      setIsSubmittingBet(false)
    }
  }, [betItems, roundFromApi, selectedPackage, clearAllBetItems, betTypeToPrizeTypeCode, prizeTypeCodeForApi, navigate, isSubmittingBet])

  return (
    <>
      <div className="c_quinary text-center position-relative d-grid gap-2">
        <Link
          to={backTo}
          state={
            backTo === '/lobby-huay/lottery-terms'
              ? state.title === 'หวยจับยี่กี'
                ? { lotteryType: 'yeekee' as const, round: state.round, time: state.time, close: state.close }
                : state.roundFromApi
                  ? { round: state.roundFromApi }
                  : { lotteryType: 'other' as const, title: state.title, logo: state.logo, round: state.round, time: state.time, close: state.close }
              : undefined
          }
          className="link_back"
        >
          <i className="fas fa-chevron-left me-1" /> {backLabel}
        </Link>
        <a
          href="#rulesModal"
          className="bg_gd_quaternary hh_hover border_5 px-2 py-1 ms-auto"
          style={{ width: 'fit-content' }}
          onClick={(e) => e.preventDefault()}
        >
          <h6 className="c_tertiary">กติกา & วิธีดูผลรางวัล</h6>
        </a>
      </div>

      <div className="row mx__1 mt-3 huay-bet-page" style={{ rowGap: '1rem' }}>
        <ModernLobbyHuayBetSideLeft
          title={title}
          logo={logo}
          roundLabel={roundLabel}
          countdown={countdownText}
          round={roundFromApi}
          selectedPackage={selectedPackage}
          selectedBetType={selectedBetType}
          setSelectedBetType={setSelectedBetType}
          numbers={numbers}
          handleNumberInput={handleNumberInput}
          appendDigit={appendDigit}
          backspace={backspace}
          addBetItem={addBetItem}
          betItems={betItems}
          defaultAmount={defaultAmount}
          setNumbers={setNumbers}
          clearSelectionRef={clearLeftSelectionRef}
        />
        <ModernLobbyHuayBetSideRight
          countdown={countdownText}
          betItems={betItems}
          removeBetItem={removeBetItem}
          updateBetAmount={updateBetAmount}
          clearAllBetItems={clearAllBetItems}
          submitBetItems={submitBetItems}
          isSubmittingBet={isSubmittingBet}
          defaultAmount={defaultAmount}
          setDefaultAmount={setDefaultAmount}
          creditBalance={walletBalance}
          isLoadingWallet={isLoadingWallet}
          selectedPackage={selectedPackage}
          round={roundFromApi}
        />
      </div>
    </>
  )
}

export default ModernLobbyHuayBetPage
